import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import createEvidenceBundle from 'fetchers/evidence/createEvidenceBundle';
import getEvidenceBundleStatus from 'fetchers/evidence/getEvidenceBundleStatus';
import completeEvidenceBundle from 'fetchers/evidence/completeEvidenceBundle';
import triggerAnchorDownload from 'components/Complaints/Complaint/EvidenceV2/download/triggerAnchorDownload';

/**
 * App-level evidence-bundle download manager.
 *
 * The create → poll → download flow lives here, mounted once ABOVE the router
 * (see App.component.js), so a bundle keeps building and downloads even after
 * the user navigates away from the evidence page. Multiple concurrent bundles
 * are supported — each is a {@link DownloadJob} tracked by its own poll loop.
 *
 * The actual progress UI is {@link DownloadManagerWidget}; components kick a
 * download off via {@link useDownloadManager} (or the thin `useEvidenceBundle`
 * wrapper) and never own the polling themselves.
 */

/**
 * Tiered poll backoff, keyed on elapsed time since polling began: poll fast
 * early (most bundles finish in seconds), then ease off so long / multi-GB
 * builds don't hammer the status endpoint. Edit the table to retune.
 */
const POLL_SCHEDULE: ReadonlyArray<{ untilMs: number; everyMs: number }> = [
  { untilMs: 10_000, everyMs: 2_000 }, // first 10s → every 2s
  { untilMs: 60_000, everyMs: 4_000 }, // 10s–1min → every 4s
];
/** After the last tier (here: > 1 min) poll at this cadence. */
const POLL_FALLBACK_MS = 6_000;
/** Overall cap — give up after this long (matches the lambda's max). */
const MAX_POLL_DURATION_MS = 10 * 60_000;

/**
 * If a bundle is still PENDING (the worker has never advanced it to PROCESSING)
 * after this long, the queue/worker isn't consuming — fail fast rather than wait
 * out the cms stale sweep or the overall poll cap. The lambda normally picks a
 * job up within seconds; once it reports PROCESSING this no longer applies and
 * an actively-building bundle polls until MAX_POLL_DURATION_MS.
 */
const PENDING_PICKUP_TIMEOUT_MS = 30_000;

const nextPollDelay = (elapsedMs: number): number => POLL_SCHEDULE.find((tier) => elapsedMs < tier.untilMs)?.everyMs ?? POLL_FALLBACK_MS;

export type DownloadPhase = 'creating' | 'pending' | 'processing' | 'ready' | 'failed' | 'expired' | 'cancelled';

/**
 * Phases where a bundle is still being requested, queued or built — i.e. a
 * download is genuinely "in progress" for the case. `ready` and the failure
 * phases are terminal from the manager's point of view (the ZIP has been handed
 * to the browser, or the job needs an explicit retry), so they don't count.
 * Consumers use this to keep the Download button disabled for the whole
 * lifetime of an in-flight job — see {@link isDownloadInProgress}.
 */
const ACTIVE_DOWNLOAD_PHASES: ReadonlySet<DownloadPhase> = new Set<DownloadPhase>(['creating', 'pending', 'processing']);

/** True while the given phase represents an in-flight (not-yet-finished) download. */
export const isDownloadInProgress = (phase: DownloadPhase): boolean => ACTIVE_DOWNLOAD_PHASES.has(phase);

export interface DownloadJob {
  /** Client-side id for this download (distinct from the server bundleId). */
  id: string;
  /** Complaint this bundle belongs to — lets a surface tell whether *its* case
   *  already has an in-flight download (so the button stays disabled across
   *  navigation) without blocking downloads on other cases. */
  complaintId: string;
  /** Human label shown in the widget, e.g. "Evidence bundle (4 files)". */
  label: string;
  phase: DownloadPhase;
  /** Set once the bundle is READY. */
  fileName?: string;
  /** Set when phase === 'failed'. */
  error?: string;
}

interface StartDownloadArgs {
  complaintId: string;
  evidenceIds: string[];
  label?: string;
}

export interface DownloadManagerValue {
  jobs: DownloadJob[];
  /** Begin a bundle download; returns the client job id so callers can track it. */
  startDownload: (args: StartDownloadArgs) => string;
  /** Remove a finished/failed job card from the widget (does not cancel server-side). */
  dismiss: (jobId: string) => void;
  /** Re-run a failed/expired/cancelled job with the same evidence selection. */
  retry: (jobId: string) => void;
  /** Stop tracking an in-progress job client-side (the lambda keeps zipping
   *  server-side); moves it to the 'cancelled' phase so it can be re-run. */
  cancel: (jobId: string) => void;
}

const DownloadManagerContext = createContext<DownloadManagerValue | null>(null);

/** No-op fallback used when no provider is mounted (e.g. isolated component
 *  tests). The real provider is always mounted at app root in production. */
const NOOP_MANAGER: DownloadManagerValue = {
  jobs: [],
  startDownload: () => '',
  dismiss: () => undefined,
  retry: () => undefined,
  cancel: () => undefined,
};

export const useDownloadManager = (): DownloadManagerValue => {
  return useContext(DownloadManagerContext) ?? NOOP_MANAGER;
};

// ── Per-job runtime ──────────────────────────────────────────────────────────

interface DownloadJobRuntime {
  complaintId: string;
  evidenceIds: string[];
  bundleId: string | null;
  attempts: number;
  /** True once the worker has reported PROCESSING — disarms the pending-pickup bail. */
  sawProcessing: boolean;
  /** Epoch ms when polling started — drives the tiered backoff + overall cap. */
  pollStartedAt: number;
  timer: ReturnType<typeof setTimeout> | null;
}

export const DownloadManagerProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const [jobs, setJobs] = useState<DownloadJob[]>([]);

  // Per-job mutable runtime (timers, attempt counts, bundleId, original args
  // for retry). Kept in a ref so re-renders never reset in-flight polling.
  const runtimeRef = useRef<Map<string, DownloadJobRuntime>>(new Map());
  const mountedRef = useRef(true);

  useEffect(() => {
    // Snapshot the runtime map for the cleanup closure (refs are stable, but
    // capturing it here keeps the exhaustive-deps lint honest).
    const runtimes = runtimeRef.current;
    return () => {
      mountedRef.current = false;
      runtimes.forEach((r) => r.timer && clearTimeout(r.timer));
      runtimes.clear();
    };
  }, []);

  const patchJob = useCallback((id: string, patch: Partial<DownloadJob>) => {
    if (!mountedRef.current) return;
    setJobs((prev) => prev.map((j) => (j.id === id ? { ...j, ...patch } : j)));
  }, []);

  const clearTimer = useCallback((id: string) => {
    const rt = runtimeRef.current.get(id);
    if (rt?.timer) {
      clearTimeout(rt.timer);
      rt.timer = null;
    }
  }, []);

  const handleReady = useCallback(
    (id: string, downloadUrl: string, fileName: string, bundleId: string) => {
      clearTimer(id);

      // Native browser download: the presigned URL carries Content-Disposition,
      // so the browser streams the ZIP straight to disk — its own progress UI,
      // immediate start, no in-memory buffering (safe for very large bundles).
      triggerAnchorDownload(downloadUrl);
      patchJob(id, { phase: 'ready', fileName });

      // Best-effort mark-consumed; the bucket lifecycle is the backstop.
      completeEvidenceBundle(bundleId).catch(() => undefined);
    },
    [clearTimer, patchJob],
  );

  const pollJob = useCallback(
    (id: string) => {
      const rt = runtimeRef.current.get(id);
      if (!rt || !rt.bundleId) return;

      const delay = nextPollDelay(Date.now() - rt.pollStartedAt);
      rt.timer = setTimeout(async () => {
        if (!mountedRef.current) return;
        const current = runtimeRef.current.get(id);
        if (!current || !current.bundleId) return;

        current.attempts += 1;
        // Overall time cap (intervals now vary, so we cap on elapsed time, not count).
        if (Date.now() - current.pollStartedAt > MAX_POLL_DURATION_MS) {
          clearTimer(id);
          patchJob(id, { phase: 'failed', error: 'Timed out preparing the download.' });
          return;
        }

        try {
          const status = await getEvidenceBundleStatus(current.bundleId);
          if (!mountedRef.current) return;

          switch (status.bundleStatus) {
            case 'READY':
              if (status.downloadUrl && status.fileName) {
                handleReady(id, status.downloadUrl, status.fileName, current.bundleId);
              } else {
                clearTimer(id);
                patchJob(id, { phase: 'failed', error: 'Download was not ready.' });
              }
              break;
            case 'FAILED':
              clearTimer(id);
              patchJob(id, { phase: 'failed', error: 'The download failed.' });
              break;
            case 'EXPIRED':
              clearTimer(id);
              patchJob(id, { phase: 'expired' });
              break;
            case 'CONSUMED':
              // Already downloaded/consumed server-side — terminal; stop polling
              // so we never spin to the timeout cap and show a false error.
              clearTimer(id);
              break;
            case 'PROCESSING':
              // Worker has picked the job up and is actively building — show
              // progress and keep polling (large bundles legitimately take a
              // while, capped by MAX_POLL_DURATION_MS).
              current.sawProcessing = true;
              patchJob(id, { phase: 'processing' });
              pollJob(id);
              break;
            case 'PENDING':
              // Never picked up: still PENDING (never advanced to PROCESSING)
              // past the pickup window means the queue/worker isn't consuming —
              // fail fast instead of waiting out the stale sweep / poll cap.
              if (!current.sawProcessing && Date.now() - current.pollStartedAt > PENDING_PICKUP_TIMEOUT_MS) {
                clearTimer(id);
                patchJob(id, {
                  phase: 'failed',
                  error: 'The download didn’t start. Please try again.',
                });
                break;
              }
              pollJob(id); // keep polling
              break;
            default:
              // Unknown / forward-compat status — keep polling.
              pollJob(id);
              break;
          }
        } catch {
          // Transient network error — keep retrying until the cap.
          if (mountedRef.current) pollJob(id);
        }
      }, delay);
    },
    [clearTimer, handleReady, patchJob],
  );

  const beginJob = useCallback(
    async (id: string) => {
      const rt = runtimeRef.current.get(id);
      if (!rt) return;
      try {
        const { bundleId } = await createEvidenceBundle({
          complaintId: rt.complaintId,
          evidenceIds: rt.evidenceIds,
        });
        if (!mountedRef.current) return;
        rt.bundleId = bundleId;
        rt.attempts = 0;
        rt.sawProcessing = false;
        rt.pollStartedAt = Date.now();
        patchJob(id, { phase: 'pending' });
        pollJob(id);
      } catch {
        patchJob(id, { phase: 'failed', error: 'Could not start the download.' });
      }
    },
    [patchJob, pollJob],
  );

  const startDownload = useCallback(
    ({ complaintId, evidenceIds, label }: StartDownloadArgs): string => {
      const id = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `dl-${Date.now()}-${Math.round(Math.random() * 1e6)}`;

      runtimeRef.current.set(id, {
        complaintId,
        evidenceIds,
        bundleId: null,
        attempts: 0,
        sawProcessing: false,
        pollStartedAt: 0,
        timer: null,
      });

      setJobs((prev) => [
        ...prev,
        {
          id,
          complaintId,
          label: label ?? `Evidence bundle (${evidenceIds.length} files)`,
          phase: 'creating',
        },
      ]);

      void beginJob(id);
      return id;
    },
    [beginJob],
  );

  const dismiss = useCallback(
    (id: string) => {
      clearTimer(id);
      runtimeRef.current.delete(id);
      setJobs((prev) => prev.filter((j) => j.id !== id));
    },
    [clearTimer],
  );

  const retry = useCallback(
    (id: string) => {
      const rt = runtimeRef.current.get(id);
      if (!rt) return;
      clearTimer(id);
      rt.bundleId = null;
      rt.attempts = 0;
      rt.sawProcessing = false;
      patchJob(id, { phase: 'creating', error: undefined });
      void beginJob(id);
    },
    [beginJob, clearTimer, patchJob],
  );

  const cancel = useCallback(
    (id: string) => {
      const rt = runtimeRef.current.get(id);
      if (!rt) return;
      // Stop the local poll loop. The lambda may still finish zipping
      // server-side, but the user has chosen to stop waiting — the runtime
      // (complaintId / evidenceIds) is kept so they can "Download again".
      clearTimer(id);
      rt.bundleId = null;
      rt.attempts = 0;
      patchJob(id, { phase: 'cancelled', error: undefined });
    },
    [clearTimer, patchJob],
  );

  return <DownloadManagerContext.Provider value={{ jobs, startDownload, dismiss, retry, cancel }}>{children}</DownloadManagerContext.Provider>;
};

export default DownloadManagerProvider;
